import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Sparkles } from 'lucide-react';
import HRAssistantChat from './HRAssistantChat';
import { useAuth } from './AuthContext';
import { getPrimaryRole } from '../utils/permissions';

const MIN_W = 360;
const MIN_H = 420;
const DEFAULT_W = 560;
const DEFAULT_H = 680;
const STORAGE_KEY = 'hra_popup_size_v3';

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const loadSize = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { w: DEFAULT_W, h: DEFAULT_H };
    const parsed = JSON.parse(raw);
    return {
      w: Number(parsed.w) || DEFAULT_W,
      h: Number(parsed.h) || DEFAULT_H,
    };
  } catch {
    return { w: DEFAULT_W, h: DEFAULT_H };
  }
};

export default function ChatPopup() {
  const { user } = useAuth();
  const role = getPrimaryRole(user);
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState(loadSize);
  const [resizing, setResizing] = useState(null); // 'left' | 'top' | 'corner' | null
  const dragRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Persist size when it changes.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(size));
    } catch { /* ignore */ }
  }, [size]);

  // Window-level listeners during a drag.
  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const maxW = window.innerWidth - 48;
      const maxH = window.innerHeight - 120;
      let w = d.startW;
      let h = d.startH;
      if (d.mode === 'left' || d.mode === 'corner') {
        w = clamp(d.startW + (d.startX - e.clientX), MIN_W, maxW);
      }
      if (d.mode === 'top' || d.mode === 'corner') {
        h = clamp(d.startH + (d.startY - e.clientY), MIN_H, maxH);
      }
      setSize({ w, h });
    };
    const onUp = () => {
      setResizing(null);
      dragRef.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  const startResize = useCallback((mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startW: size.w,
      startH: size.h,
    };
    setResizing(mode);
    document.body.style.userSelect = 'none';
    document.body.style.cursor =
      mode === 'left' ? 'ew-resize' : mode === 'top' ? 'ns-resize' : 'nwse-resize';
  }, [size]);

  // Show for all authenticated roles
  if (!role) return null;

  const panelWidth = `min(${size.w}px, calc(100vw - 48px))`;
  const panelHeight = `min(${size.h}px, calc(100vh - 120px))`;

  return (
    <>
      {/* Invisible backdrop — catches outside clicks */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1199 }}
        />
      )}

      {/* Floating card */}
      <div style={{
        position: 'fixed',
        right: '24px',
        bottom: '96px',
        width: panelWidth,
        height: panelHeight,
        background: 'var(--theme-surface)',
        borderRadius: '20px',
        border: '1px solid rgba(99, 102, 241, 0.28)',
        boxShadow: '0 24px 64px rgba(30, 27, 75, 0.22), 0 4px 18px rgba(99, 102, 241, 0.18), 0 0 0 1px rgba(139, 92, 246, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 1200,
        /* Pop-out animation */
        opacity: open ? 1 : 0,
        transform: open ? 'scale(1) translateY(0)' : 'scale(0.94) translateY(16px)',
        transformOrigin: 'bottom right',
        pointerEvents: open ? 'auto' : 'none',
        transition: resizing
          ? 'opacity 0.25s ease'
          : 'opacity 0.25s ease, transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.12s ease, height 0.12s ease',
      }}>
        {/* Left-edge resize handle (drag to widen) */}
        <div
          onMouseDown={startResize('left')}
          title="Drag to resize width"
          style={{
            position: 'absolute',
            left: 0,
            top: 20,
            bottom: 20,
            width: 8,
            cursor: 'ew-resize',
            zIndex: 5,
            background: 'transparent',
          }}
        >
          <div style={{
            position: 'absolute',
            left: 2,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 4,
            height: 44,
            borderRadius: 3,
            background: resizing === 'left' ? '#6366f1' : 'rgba(99, 102, 241, 0.35)',
            transition: 'background 0.15s',
          }} />
        </div>

        {/* Top-edge resize handle (drag to grow taller) */}
        <div
          onMouseDown={startResize('top')}
          title="Drag to resize height"
          style={{
            position: 'absolute',
            left: 20,
            right: 20,
            top: 0,
            height: 6,
            cursor: 'ns-resize',
            zIndex: 5,
            background: 'transparent',
          }}
        />

        {/* Top-left corner handle (diagonal resize) */}
        <div
          onMouseDown={startResize('corner')}
          title="Drag to resize"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: 16,
            height: 16,
            cursor: 'nwse-resize',
            zIndex: 6,
          }}
        />
        {/* Card header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 18px',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          flexShrink: 0,
          borderRadius: '20px 20px 0 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
            <div style={{
              width: '34px',
              height: '34px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Sparkles size={16} color="white" />
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '14px', lineHeight: 1.2 }}>
                HR Assistant
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', marginTop: '2px' }}>
                Payslips, leave, attendance &amp; more
              </div>
            </div>
          </div>

          <button
            onClick={() => setOpen(false)}
            aria-label="Close assistant"
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              borderRadius: '8px',
              padding: '7px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              transition: 'background 0.15s',
              flexShrink: 0,
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            <X size={16} />
          </button>
        </div>

        {/* Rich accent strip — corporate gradient separator */}
        <div style={{
          height: '2px',
          flexShrink: 0,
          background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 35%, #ec4899 65%, #f59e0b 100%)',
          opacity: 0.85,
        }} />

        {/* Chat content */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <HRAssistantChat />
        </div>
      </div>

      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open AI Assistant"
          style={{
            position: 'fixed',
            bottom: '28px',
            right: '28px',
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 20px rgba(99,102,241,0.45)',
            zIndex: 1201,
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'scale(1.08)';
            e.currentTarget.style.boxShadow = '0 6px 28px rgba(99,102,241,0.55)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(99,102,241,0.45)';
          }}
        >
          <MessageCircle size={24} color="white" />
        </button>
      )}
    </>
  );
}
